import { FC, useState } from 'react';
import intl from 'react-intl-universal';
import {
  tailwindOverride,
  TableRow,
  IconButton,
  Tooltip,
  decimalHoursToTimeString,
} from '@getsynapse/design-system';
import { getStatusColumn } from 'Pages/ProjectsListPage/helpers/tableColumnsValues';
import { TASK_STATUS, PATHS } from 'utils/constants';
import {
  TaskListElement,
  TaskWithAssigneesAndProject,
} from 'types/store/tasksList';
import { CentralizedTasksTableTab } from 'utils/types/centralizedTasks';
import { CENTRALIZED_TASKS_TABLE_TABS } from 'utils/constants/centralizedTasks';
import AssigneesColumn from './AssigneesColumn/AssigneesColumn';
import ProjectColumn from './ProjectColumn/ProjectColumn';
import QuickHoursAddingAction from 'Organisms/QuickHoursAddingAction';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LinkTableRowCell } from 'Atoms/LinkTableRowCell';
import {
  AssigneeTableRowCell,
  HoursTableRowCell,
} from 'Atoms/EditableTableRowCell';
import { useUpdateCells } from 'Pages/ProjectTasksList/TasksTable/hooks/useUpdateCells';
import { useDispatch, useSelector } from 'react-redux';
import { updateTaskInList as updateTeamTaskInList } from 'state/normalized/tasksList/teamTasks/teamTasksSlice';
import { updateTaskInList as updateMyTaskInList } from 'state/normalized/tasksList/myTasks/myTasksSlice';
import { PilotPagesToTrack } from 'utils/customTypes';
import { StatusTableRowCell } from 'Atoms/EditableTableRowCell/StatusTableRowCell';
import { setTasksAssignees } from 'state/normalized/tasksList/allTasks/allTasksSlice';
import { TaskAssignee } from 'types/store/tasks';
import {
  getIsProjectCanceled,
  getIsProjectClosed,
} from 'state/Project/projectSlice';

interface TaskRowProps {
  task: TaskWithAssigneesAndProject;
  bgColor: string;
  openTakInSidePanel: (taskId: string) => void;
  refreshTasks: () => void;
  tableName: CentralizedTasksTableTab;
}

const TaskRow: FC<TaskRowProps> = ({
  task,
  bgColor,
  openTakInSidePanel,
  refreshTasks,
  tableName,
}) => {
  const { inlineEditingTaskAssignee, flexibleTaskTimeEntry } = useFlags();

  const [isAssigneesDropdownOpened, setIsAssigneesDropdownOpened] =
    useState<boolean>(false);

  const isProjectClosed = useSelector(getIsProjectClosed);
  const isProjectCanceled = useSelector(getIsProjectCanceled);

  const columnStyles = tailwindOverride(
    'border-b border-t border-r-0 border-transparent',
    'px-4 py-3'
  );

  const stickyColumnStyles = tailwindOverride(
    columnStyles,
    bgColor,
    'sticky z-1'
  );

  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const formatDate = (dateString: string): string => {
    return dateFormatter.format(new Date(dateString));
  };
  const hasPermission = task.permissions?.canEdit;
  const isOnHold = task.status === TASK_STATUS.ON_HOLD;
  const canInlineEditTask = !isOnHold && !task.disabled;

  const isTaskProjectDisabled = isProjectClosed || isProjectCanceled;

  const canEditStatus =
    !task.disabled && !isTaskProjectDisabled && hasPermission;

  const canEdit =
    !isOnHold && !isTaskProjectDisabled && hasPermission && !task.disabled;

  const isViewOnlyMode = !canEdit;
  const isOnlyStatusViewMode =
    !task.permissions?.canEdit || !canEditStatus || isTaskProjectDisabled;

  const dispatch = useDispatch();

  const updateTeamTask = (
    taskId: string,
    updatedTask: Partial<TaskListElement>
  ) => {
    dispatch(
      updateTeamTaskInList({
        taskId,
        updatedTask,
      })
    );
  };

  const updateMyTask = (
    taskId: string,
    updatedTask: Partial<TaskListElement>
  ) => {
    dispatch(
      updateMyTaskInList({
        taskId,
        updatedTask,
      })
    );
  };

  const updateAssignees = (updatedAssignees: TaskAssignee[]) => {
    dispatch(setTasksAssignees(updatedAssignees));
  };

  const getActualHours = (hours?: string) => {
    if (flexibleTaskTimeEntry) {
      return decimalHoursToTimeString(parseFloat(hours || '0'));
    }

    return hours ? `${parseFloat(hours)}h` : '0h';
  };

  const {
    handleSaveEstimateHours,
    handleSaveTaskStatus,
    statuses,
    handleSaveAssignees,
    handleSearchAssignees,
    availableAssignees,
    isFetchingAvailableAssignees,
  } = useUpdateCells({
    isAssigneesDropdownOpened,
    canUpdateTask: !!canEdit,
    canUpdateStatus: canEditStatus,
    isViewOnly: isViewOnlyMode,
    task,
    updateTaskInList:
      tableName === CENTRALIZED_TASKS_TABLE_TABS.TEAM_TASKS
        ? updateTeamTask
        : updateMyTask,
    updateAssignees,
  });

  return (
    <TableRow
      id={task.id}
      aria-label={`task-row-${task.id}`}
      className={tailwindOverride('group relative', bgColor)}
      cells={[
        {
          content: <div className='w-18 min-w-18'>{task.displayId}</div>,
          className: tailwindOverride(stickyColumnStyles, 'w-18 left-0'),
        },
        {
          content: (
            <LinkTableRowCell
              value={task.name}
              to={`${PATHS.PROJECT_PAGE}/${task.project.id}/tasks/${task.id}`}
              className='w-70'
            />
          ),
          className: tailwindOverride(stickyColumnStyles, 'w-70 left-26'),
        },
        {
          content: (
            <StatusTableRowCell
              value={task.status}
              customizedValue={(value) =>
                getStatusColumn(value, false, '', true, canEditStatus)
              }
              options={statuses}
              isEditable={!isOnlyStatusViewMode}
              className='w-32'
              isTaskProjectDisabled={isTaskProjectDisabled}
              canInlineEditTask={!task.disabled}
              onSave={handleSaveTaskStatus}
              canEdit={canEditStatus}
              userPilotEventOrigin={PilotPagesToTrack.CENTRALIZED_TASK}
            />
          ),
          className: tailwindOverride(columnStyles, 'w-32 p-0'),
        },
        {
          content: (
            <div className='truncate w-72'>
              <ProjectColumn taskProject={task.project} />
            </div>
          ),
          className: tailwindOverride(columnStyles, 'w-72'),
        },
        {
          content: (
            <div className='truncate w-44'>
              {inlineEditingTaskAssignee ? (
                <AssigneeTableRowCell
                  currentAssignees={task.assignees}
                  availableAssignees={availableAssignees}
                  isEditable={!isViewOnlyMode}
                  hasPermission={hasPermission}
                  onSave={handleSaveAssignees}
                  onSearchChange={handleSearchAssignees}
                  isTaskProjectDisabled={isTaskProjectDisabled}
                  canInlineEditTask={canInlineEditTask}
                  setIsTimeToFetchData={setIsAssigneesDropdownOpened}
                  isFetchingAvailableAssignees={isFetchingAvailableAssignees}
                />
              ) : (
                <AssigneesColumn assignees={task.assignees} />
              )}
            </div>
          ),
          className: tailwindOverride(columnStyles, 'w-44', {
            'p-0': inlineEditingTaskAssignee,
          }),
        },
        {
          content: (
            <div className='truncate w-32'>
              {task.startDate
                ? formatDate(task.startDate)
                : intl.get('TASKS.TABLE.NO_DATE')}
            </div>
          ),
          className: tailwindOverride(columnStyles, 'w-32'),
        },
        {
          content: (
            <div className='truncate w-32'>
              {task.endDate
                ? formatDate(task.endDate)
                : intl.get('TASKS.TABLE.NO_DATE')}
            </div>
          ),
          className: tailwindOverride(columnStyles, 'w-32'),
        },
        {
          content: (
            <div className='truncate w-32'>
              <HoursTableRowCell
                hasPermission={hasPermission}
                value={parseFloat(task.estimateHours || '0')}
                isEditable={!isViewOnlyMode}
                onSave={handleSaveEstimateHours}
                isTaskProjectDisabled={isTaskProjectDisabled}
                canInlineEditTask={canInlineEditTask}
                className='px-4 py-3'
                userPilotEventOrigin={PilotPagesToTrack.CENTRALIZED_TASK}
              />
            </div>
          ),
          className: tailwindOverride(columnStyles, 'w-32', 'p-0'),
        },
        {
          content: (
            <div className='truncate w-32'>
              {getActualHours(task.actualHours)}
            </div>
          ),
          className: tailwindOverride(columnStyles, 'w-32'),
        },
        {
          content: (
            <div
              className='w-20 h-full flex items-center justify-end gap-x-1 relative'
              onClick={(e) => e.stopPropagation()}
            >
              <div className='hidden group-hover:flex items-center gap-x-1'>
                <Tooltip
                  openMode='hover1'
                  timeout={0}
                  showPopper
                  position='topCenter'
                  contentProps={{
                    className: 'px-3 py-2 text-sm z-50 absolute',
                  }}
                  usePortal
                  trigger={
                    <IconButton
                      name={!isViewOnlyMode ? 'pencil-outline' : 'eye'}
                      aria-label={`task-row-${task.id}-cta-button`}
                      iconClassname='pointer-events-none'
                      className='text-base text-neutral-dark p-1 hover:bg-neutral-lightest hover:shadow-allocation-table rounded'
                      onClick={() => openTakInSidePanel(task.id)}
                    />
                  }
                >
                  {!isViewOnlyMode
                    ? intl.get('SIDE_PANEL.QUICK_EDIT')
                    : intl.get('SIDE_PANEL.QUICK_VIEW')}
                </Tooltip>
                <QuickHoursAddingAction
                  task={task}
                  refreshTasks={refreshTasks}
                  userPilotEventOrigin={PilotPagesToTrack.CENTRALIZED_TASK}
                />
              </div>
            </div>
          ),
          className: tailwindOverride(stickyColumnStyles, 'w-20 right-0'),
        },
      ]}
    />
  );
};

export default TaskRow;
